import { useEffect } from "react";
import type { Transaction, TransactionGroup } from "../shared/types";
import { money, navigate } from "../shared/ui";
import { transactionGroups, transactionPath } from "./transaction-groups";
import { transactionCategoryGlyph, transactionManagementDate, transactionManagementStatus } from "./transaction-management-presentation";
import { restoreTransactionListFocus } from "./transaction-list-focus";
import "./TransactionManagementList.css";

export interface TransactionManagementListProps {
  readonly items: readonly Transaction[];
  readonly group: TransactionGroup;
  readonly counts: readonly { readonly group: TransactionGroup; readonly count: number }[];
  readonly onGroupChange: (group: TransactionGroup) => void;
  readonly onRegister: () => void;
  readonly restoreFocus?: boolean;
}

/** Reference list cards open the detail sheet; financial actions belong to the sheet. */
export function TransactionManagementList({ items, group, counts, onGroupChange, onRegister, restoreFocus = true }: TransactionManagementListProps) {
  useEffect(() => {
    if (restoreFocus) restoreTransactionListFocus();
  }, [items, restoreFocus]);
  return <div className="member-root-screen member-transaction-management">
    <header className="member-transaction-management__header">
      <div className="member-transaction-management__heading">
        <h1 tabIndex={-1}>거래 관리</h1>
        <button type="button" className="member-transaction-management__register" onClick={onRegister}><span aria-hidden="true">＋</span> 등록</button>
      </div>
      <div className="member-transaction-management__filters" role="group" aria-label="거래 상태 필터">
        {transactionGroups.map(({ value, label }) => <button key={value} type="button" aria-pressed={group === value} onClick={() => onGroupChange(value)}>{label} {counts.find(item => item.group === value)?.count ?? 0}</button>)}
      </div>
    </header>
    <div className="member-transaction-management__list">
      {items.length === 0 ? <div className="member-transaction-management__empty">
        <span aria-hidden="true">📭</span><p>해당하는 거래가 없어요</p>
      </div> : items.map(item => {
        const status = transactionManagementStatus(item.status, item.paymentStatus);
        return <article className="member-transaction-management__card" key={item.id}>
          <button type="button" className="member-transaction-management__row" data-transaction-id={item.id} onClick={() => navigate(transactionPath(`/app/transactions/${item.id}`, group))}>
            <span className="member-transaction-management__glyph" aria-hidden="true">{transactionCategoryGlyph(item.categoryCode)}</span>
            <span className="member-transaction-management__copy">
              <strong>{item.title}</strong>
              <span className={`member-transaction-management__badge member-transaction-management__badge--${status.tone}`}>{status.label}</span>
            </span>
            <span className="member-transaction-management__meta">
              <strong>{money(item.approvedAmount ?? item.requestedAmount)}</strong>
              <time dateTime={item.submittedAt}>{transactionManagementDate(item.submittedAt)}</time>
            </span>
          </button>
        </article>;
      })}
    </div>
  </div>;
}
